import { useEffect, useState } from "react";
import { Navigate, NavLink, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { LayoutGrid, CreditCard, Receipt, Users, BarChart3, CalendarDays, ClipboardCheck, Settings as SettingsIcon, UserPlus, Bell, Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { TopbarMenus } from "@/components/dashboard/TopbarMenus";
import { api, errorText } from "@/lib/api";
import { BRAND } from "@/brand.config";
import { TestModeBanner } from "@/components/brand/TestModeBanner";
import { PilotTermsLink } from "@/components/brand/PilotTermsLink";
import Onboarding from "./Onboarding";
import Review from "./Review";
import Settings from "./Settings";
import Overview from "./Overview";
import Subscriptions from "./Subscriptions";
import { Vendors, Transactions, Reports, Renewals } from "./MorePages";
import Team from "./Team";

const WORKSPACE = [["/app", "Overview", LayoutGrid], ["/app/subscriptions", "Subscriptions", CreditCard], ["/app/transactions", "Transactions", Receipt], ["/app/vendors", "Vendors", Users], ["/app/reports", "Reports", BarChart3], ["/app/renewals", "Renewals", CalendarDays], ["/app/review", "Review", ClipboardCheck]];
const MANAGE = [["/app/team", "Team access", UserPlus], ["/app/settings", "Settings", SettingsIcon]];

const GoogleReconnectNotice = () => {
  const [n, setN] = useState(null);
  useEffect(() => { api.get("/sources/google/status").then((r) => setN(r.data.notice)).catch(() => {}); }, []);
  if (!n) return null;
  return <div role="alert" className="text-center text-xs font-semibold py-2 px-4" style={{ background: "var(--amber-soft)", color: "var(--amber)", borderBottom: "1px solid #EAD7A8" }} data-testid="google-reconnect-notice">{n} <NavLink to="/app/settings#sources" className="underline ml-1">Open Settings</NavLink></div>;
};

export default function AppShell() {
  const { user, config } = useAuth();
  const [note, setNote] = useState("");
  const loc = useLocation();
  const nav = useNavigate();
  if (user === null) return <div className="min-h-screen" style={{ background: "var(--paper)" }} />;
  if (user === false) return <Navigate to="/signin" replace state={{ from: loc.pathname }} />;
  const done = user.onboarding_state === "done";
  if (!done && !loc.pathname.startsWith("/app/onboarding") && !loc.pathname.startsWith("/app/review")) return <Navigate to="/app/onboarding" replace />;
  if (loc.pathname.startsWith("/app/onboarding")) return <Onboarding />;

  const pages = config?.plans?.[user.plan]?.pages || ["overview", "subscriptions"];
  const isCore = (label) => !["Overview", "Review", "Settings", "Team access"].includes(label) && !pages.includes(label.toLowerCase());
  const resend = async () => { try { await api.post("/auth/resend-verification"); setNote("Sent. Check your inbox."); } catch (e) { setNote(errorText(e)); } };
  const biz = user.business?.business_name || user.full_name;
  const initials = (user.full_name || user.email).split(/[\s@]/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("");
  const Item = ([to, label, Icon]) => (
    <NavLink key={to} to={to} end={to === "/app"} data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`} className={({ isActive }) => (isActive ? "active" : "")}>
      <Icon size={18} /> {label}{isCore(label) && <span className="badge ml-auto" style={{ background: "#16233F", color: "#7DA2F5" }}>CORE</span>}
    </NavLink>
  );
  return (
    <div className="min-h-screen flex" style={{ background: "var(--paper)" }}>
      <aside className="app-side hidden lg:flex flex-col shrink-0 p-6" data-testid="app-sidebar">
        <div className="wordmark text-2xl whitespace-nowrap" style={{ color: "#fff" }}>{BRAND.wordmark}<span className="dot" /></div>
        <p className="text-sm italic mt-1" style={{ color: "var(--on-navy-soft)" }}>{BRAND.tagline}</p>
        <nav aria-label="Workspace"><p className="group">WORKSPACE</p>{WORKSPACE.map(Item)}<p className="group">MANAGE</p>{MANAGE.map(Item)}</nav>
        <div className="mt-auto rounded-xl p-4 text-sm" style={{ background: "#121B30" }} data-testid="side-plan">
          <p className="font-bold text-white" data-testid="side-plan-name">{config?.pilot_mode ? "Pilot access" : config?.plans?.[user.plan]?.name}</p>
          <p className="text-xs mt-1" style={{ color: "var(--on-navy-soft)" }}>{config?.pilot_mode ? "All features unlocked for testers" : (user.plan === "core" ? "Ongoing monitoring · monthly benchmarks" : "One-time snapshot · upgrade for monitoring")}</p>
          <PilotTermsLink className="text-xs mt-2 inline-block underline" />
        </div>
      </aside>
      <div className="flex-1 min-w-0 flex flex-col">
        <TestModeBanner mode={config?.stripe_mode} pilot={config?.pilot_mode} />
        <GoogleReconnectNotice />
        <header className="flex items-center gap-4 px-6 lg:px-10 py-4" data-testid="app-topbar">
          <div className="lg:hidden wordmark text-lg">{BRAND.wordmark}<span className="dot" /></div>
          <label className="hidden md:flex items-center gap-2 flex-1 max-w-md ts-input h-11" style={{ borderRadius: 12 }}><Search size={16} style={{ color: "var(--faint)" }} /><input className="flex-1 outline-none bg-transparent text-sm" placeholder="Search vendors, categories, documents…" data-testid="global-search" onKeyDown={(e) => { if (e.key === "Enter") nav(`/app/subscriptions?q=${encodeURIComponent(e.target.value)}`); }} /></label>
          <TopbarMenus />
        </header>
        <nav className="lg:hidden flex gap-1 overflow-x-auto px-4 pb-2 text-sm" aria-label="Pages">{[...WORKSPACE, ...MANAGE].map(([to, label]) => <NavLink key={to} to={to} end={to === "/app"} className={({ isActive }) => `chip whitespace-nowrap ${isActive ? "active" : ""}`}>{label}</NavLink>)}</nav>
        <main className="flex-1 px-6 lg:px-10 pb-12">
          {!user.email_verified && (
            <div role="status" data-testid="verify-notice" className="rounded-xl p-4 mb-6 text-sm flex flex-wrap items-center gap-3" style={{ background: "var(--blue-soft)", border: "1px solid #C9D4FF", color: "var(--blue-deep)" }}>
              <span className="font-semibold">Please confirm your email.</span><span>We sent a link to {user.email}.</span>
              <button className="ts-link text-sm" onClick={resend} data-testid="resend-verification-button">Resend</button>{note && <span data-testid="resend-note">{note}</span>}
            </div>
          )}
          <Routes>
            <Route index element={<Overview />} />
            <Route path="subscriptions" element={<Subscriptions />} />
            <Route path="transactions" element={<Transactions />} />
            <Route path="vendors" element={<Vendors />} />
            <Route path="reports" element={<Reports />} />
            <Route path="renewals" element={<Renewals />} />
            <Route path="review" element={<Review />} />
            <Route path="settings" element={<Settings />} />
            <Route path="team" element={<Team />} />
            <Route path="*" element={<Navigate to="/app" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
